<script setup lang="ts">
import { AlertCircle, CheckCircle2 } from 'lucide-vue-next'
import { Alert, AlertDescription, AlertTitle } from '~/components/ui/alert'
import { ContentBlock } from '~/components/ui/content-block'
import { Button } from '~/components/ui/button'
import { Rating } from '~/components/ui/rating'
import { Textarea } from '~/components/ui/textarea'
import { extractRatingErrorMessage, useRating } from '~/composables/useRating'
import WidgetRatingCriteriaSet from './WidgetRatingCriteriaSet.vue'
import { ANCHOR_IDS } from '~/lib/constants'

interface Entity {
  id: number
  organization_type?: string
  title?: string
}

interface Props {
  entity: Entity
  entityType?: string
  headingText?: string
  beFirstText?: string
}

const props = withDefaults(defineProps<Props>(), {
  entityType: '',
  headingText: '',
  beFirstText: ''
})

const { t } = useI18n()

const { isLoggedIn } = useUser()
const { openLoginModal } = useAuthModalStore()
const { systemSettings } = useSystemSettings()
const ratingsOnlyForAuth = computed(
  () => !!Number(systemSettings.value?.ratings_only_for_authorized)
)

const { getRatingCriteriaSets, getRatingStats, getIndividualRatings, submitRating } = useRating()

const rateableType = computed(() => {
  return props.entityType || props.entity?.organization_type || 'organization'
})

const { data: ratingSetsData, pending: ratingSetsLoading } = getRatingCriteriaSets({
  rateable_type: rateableType.value,
  status: 'active'
})

const {
  data: ratingStatsData,
  pending: ratingStatsLoading,
  refresh: refreshRatingStats
} = getRatingStats(rateableType.value, props.entity.id)

const {
  data: individualRatingsData,
  pending: _individualRatingsLoading,
  refresh: refreshIndividualRatings
} = getIndividualRatings({
  rateable_type: rateableType.value,
  rateable_id: props.entity.id,
  with_comments_only: true,
  include: ['user', 'values.criteria'],
  per_page: 10
})

const ratingSets = computed(() => {
  return ratingSetsData.value?.data || []
})

const ratingStats = computed(() => {
  return ratingStatsData.value?.data || null
})

const criteriaWithAverages = computed(() => {
  const statsArray = ratingStats.value?.criteria_stats
  const sets = ratingSets.value

  if (!statsArray || !sets || sets.length === 0) {
    return []
  }

  const criteriaList: Array<{ id: number; name: string; average: number }> = []

  sets.forEach((criteriaSet) => {
    if (criteriaSet.criteria) {
      criteriaSet.criteria.forEach((criterion) => {
        // Find the matching stat from the stats array
        const matchingStat = statsArray.find((stat) => stat.criteria_id === criterion.id)

        criteriaList.push({
          id: criterion.id,
          name: criterion.name,
          average: matchingStat?.average_rating || 0
        })
      })
    }
  })

  return criteriaList
})

const individualRatings = computed(() => {
  return (individualRatingsData.value?.data || []).filter((rating) => rating.comment)
})

const formatDate = (dateString: string) => {
  return parseSiteDate(dateString).toLocaleDateString('ru-RU', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: SITE_TIME_ZONE
  })
}

const shouldShowRating = computed(() => {
  return ratingSets.value.length > 0
})

const allRatings = ref<Record<number, Record<number, number>>>({})
const isSubmitting = ref(false)
const submitSuccess = ref(false)
const submitError = ref('')
const comment = ref('')
const { trackEvent } = useAnalytics()

const handleRatingsChanged = (criteriaSetId: number, ratings: Record<number, number>) => {
  allRatings.value[criteriaSetId] = ratings
  // Reset states when user makes changes
  if (submitSuccess.value) {
    submitSuccess.value = false
  }
  if (submitError.value) {
    submitError.value = ''
  }
}

const hasAnyRatings = computed(() => {
  return Object.values(allRatings.value).some((ratings) => Object.keys(ratings).length > 0)
})

const canSubmit = computed(() => {
  if (Object.keys(allRatings.value).length === 0 || ratingSets.value.length === 0) {
    return false
  }

  // Check that all criteria sets have complete ratings
  return ratingSets.value.every((criteriaSet) => {
    const ratings = allRatings.value[criteriaSet.id]
    if (!ratings) return false

    // All criteria in this set must be rated
    const totalCriteria = criteriaSet.criteria?.length || 0
    const ratedCriteria = Object.keys(ratings).length

    return totalCriteria > 0 && ratedCriteria === totalCriteria
  })
})

const remainingCriteriaMessage = computed(() => {
  if (canSubmit.value) return ''

  let totalCriteria = 0
  let ratedCriteria = 0

  ratingSets.value.forEach((criteriaSet) => {
    const ratings = allRatings.value[criteriaSet.id] || {}
    totalCriteria += criteriaSet.criteria?.length || 0
    ratedCriteria += Object.keys(ratings).length
  })

  const remaining = totalCriteria - ratedCriteria
  if (remaining === 0) return ''

  const word =
    remaining === 1
      ? t('widget.rating.form.criteria_word_one')
      : remaining < 5
        ? t('widget.rating.form.criteria_word_few')
        : t('widget.rating.form.criteria_word_many')
  return t('widget.rating.form.remaining_criteria', { count: remaining, word })
})

const commentLength = computed(() => comment.value.length)
const isCommentTooLong = computed(() => commentLength.value > 500)

const submitAllRatings = async () => {
  submitError.value = ''

  if (isSubmitting.value || !canSubmit.value) return

  isSubmitting.value = true
  submitSuccess.value = false

  try {
    for (const [criteriaSetId, ratings] of Object.entries(allRatings.value)) {
      if (Object.keys(ratings).length === 0) continue

      // Find the criteria set to validate completeness
      const criteriaSet = ratingSets.value.find((set) => set.id === Number(criteriaSetId))
      if (!criteriaSet) continue

      // Only submit if all criteria are rated
      const totalCriteria = criteriaSet.criteria?.length || 0
      const ratedCriteria = Object.keys(ratings).length

      if (ratedCriteria !== totalCriteria) continue

      const values = Object.entries(ratings).map(([criteriaId, rating]) => ({
        criteria_id: Number(criteriaId),
        value: rating
      }))

      const payload = {
        rateable_id: props.entity.id,
        rateable_type: rateableType.value,
        criteria_set_id: Number(criteriaSetId),
        values: values,
        ...(comment.value.trim() && { comment: comment.value.trim() })
      }

      const result = await submitRating(payload)

      if (result.error) {
        submitError.value = extractRatingErrorMessage(result.error, t('widget.rating.error.default'))
        return
      }
    }

    submitSuccess.value = true
    trackEvent('submit_review', { rateable_type: rateableType.value, product_id: props.entity.id })
    allRatings.value = {} // Reset ratings after successful submission
    comment.value = '' // Reset comment after successful submission

    // Refresh rating data after successful submission
    await Promise.all([refreshRatingStats(), refreshIndividualRatings()])

    // Don't auto-hide success message - keep it visible
  } catch (error: unknown) {
    submitError.value = extractRatingErrorMessage(error, t('widget.rating.error.default'))
  } finally {
    isSubmitting.value = false
  }
}
</script>

<template>
  <section v-if="shouldShowRating && !ratingSetsLoading">
    <ContentBlock
      :id="ANCHOR_IDS.WIDGET_RATING"
      :heading-text="headingText || $t('widget.rating.heading')"
      heading-class="h4"
      heading-tag="h2"
      variant="white"
      :show-link-icon="true"
      class="FormScrollOffset"
    >
      <!-- Rating Stats Section -->
      <div v-if="!ratingStatsLoading" class="mb-8 rounded-lg bg-gray-50 p-6">
        <div v-if="ratingStats && ratingStats.total_ratings > 0" class="space-y-4">
          <!-- Overall Rating -->
          <div class="flex items-center justify-between">
            <div
              class="flex items-center gap-4 [@media(max-width:400px)]:flex-col [@media(max-width:400px)]:items-start [@media(max-width:400px)]:gap-2"
            >
              <div class="flex items-center gap-2">
                <Rating :value="Number(ratingStats.overall_average.toFixed(1))" size="md" />
                <span class="text-2xl font-bold text-gray-900">{{
                  ratingStats.overall_average.toFixed(1)
                }}</span>
              </div>
              <div class="text-sm text-gray-600">
                {{
                  ratingStats.total_ratings === 1
                    ? $t('widget.rating.stats.reviews_one')
                    : $t('widget.rating.stats.based_on', {
                        count_total: ratingStats.total_ratings,
                        count_with_comments: individualRatings.length
                      })
                }}
              </div>
            </div>
          </div>

          <!-- Criteria Statistics -->
          <div v-if="criteriaWithAverages.length > 0">
            <div class="flex flex-row flex-wrap gap-x-6 gap-y-4 md:gap-x-0">
              <div v-for="criteria in criteriaWithAverages" :key="criteria.id" class="md:w-1/5">
                <span class="block text-sm text-gray-600">{{ criteria.name }}</span>
                <div class="flex items-center gap-2">
                  <Rating :value="Number(criteria.average.toFixed(1))" size="sm" />
                  <span class="text-sm font-medium text-gray-900">
                    {{ criteria.average.toFixed(1) }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- Individual Ratings with Comments -->
          <div v-if="individualRatings.length > 0" class="border-t pt-4">
            <Heading tag="h3" class="mb-4 text-sm font-medium text-gray-700">
              {{ $t('widget.rating.stats.reviews_with_comments') }}
            </Heading>
            <div class="space-y-6">
              <div
                v-for="rating in individualRatings"
                :key="rating.id"
                class="rounded-lg border bg-gray-50 p-4"
              >
                <!-- Rating Values by Criteria -->
                <div class="mb-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <div
                    v-for="value in rating.values"
                    :key="value.criteria_id"
                    class="flex items-center justify-between sm:grid sm:grid-cols-2 sm:gap-6 md:gap-12 [@media(max-width:400px)]:flex-col [@media(max-width:400px)]:items-start"
                  >
                    <span class="text-sm text-gray-600">{{
                      value.criteria?.name ||
                      `${$t('widget.rating.criteria.criteria_word')} ${value.criteria_id}`
                    }}</span>
                    <div class="flex items-center gap-2">
                      <Rating :value="Number(value.value.toFixed(1))" size="sm" />
                      <span class="text-sm font-medium text-gray-900">{{
                        value.value.toFixed(1)
                      }}</span>
                    </div>
                  </div>
                </div>

                <!-- Comment -->
                <div v-if="rating.comment" class="border-t border-gray-200 pt-3">
                  <p class="leading-relaxed text-gray-700">{{ rating.comment }}</p>
                </div>

                <!-- Date -->
                <div class="mt-3 flex items-center justify-between text-xs text-gray-500">
                  <span>{{ formatDate(rating.created_at) }}</span>
                  <span v-if="rating.rater_name">{{ rating.rater_name }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-else class="flex items-center gap-2">
          <div class="flex items-center gap-2">
            <Rating :value="0" size="md" />
            <span class="text-2xl font-bold text-gray-400">0.0</span>
          </div>
          <div class="text-sm text-gray-500">
            {{ beFirstText || $t(`widget.rating.stats.be_first_${entityType}`) }}
          </div>
        </div>
      </div>

      <div class="space-y-6">
        <template v-for="criteriaSet in ratingSets" :key="criteriaSet.id">
          <div class="BorderDivider">
            <Heading tag="h3" class="mb-4 text-lg font-semibold text-gray-900">
              {{ criteriaSet.name }}
            </Heading>

            <WidgetRatingCriteriaSet
              :criteria-set="criteriaSet"
              @ratings-changed="handleRatingsChanged(criteriaSet.id, $event)"
            />
          </div>
        </template>
      </div>

      <!-- Success Message (replaces button section) -->
      <Alert v-if="submitSuccess" variant="green" class="mt-6">
        <CheckCircle2 class="h-4 w-4" />
        <AlertTitle>{{ $t('widget.rating.success.title') }}</AlertTitle>
        <AlertDescription>{{ $t('widget.rating.success.message') }}</AlertDescription>
      </Alert>

      <!-- Rating Button Section -->
      <div v-else-if="hasAnyRatings" class="mt-6">
        <div
          v-if="ratingsOnlyForAuth && !isLoggedIn"
          class="border-t border-gray-200 py-8 text-center"
        >
          <p class="mb-4 text-gray-600">
            {{ $t('widget.rating.login_prompt.text') }}
          </p>
          <Button variant="blue" class="px-6 py-2" @click="openLoginModal">
            {{ $t('common.sign_in') }}
          </Button>
        </div>

        <template v-else>
          <!-- Submit Error -->
          <Alert v-if="submitError" variant="red" class="mb-4">
            <AlertCircle class="h-4 w-4" />
            <AlertDescription>{{ submitError }}</AlertDescription>
          </Alert>
          <!-- Comment Section -->
          <div class="mb-4">
            <label for="rating-comment" class="mb-2 block text-sm font-medium text-gray-700">
              {{ $t('widget.rating.form.comment_label') }}
            </label>
            <Textarea
              id="rating-comment"
              v-model="comment"
              :maxlength="500"
              :placeholder="$t('widget.rating.form.comment_placeholder')"
              class="resize-none"
              :class="{ 'border-red-300': isCommentTooLong }"
            />
            <div class="mt-1 flex justify-between text-sm">
              <span v-if="isCommentTooLong" class="text-red-600">
                {{ $t('widget.rating.form.comment_too_long') }}
              </span>
              <span v-else class="text-gray-500"> {{ $t('widget.rating.form.comment_max') }} </span>
              <span
                :class="{ 'text-red-600': isCommentTooLong, 'text-gray-400': !isCommentTooLong }"
              >
                {{ commentLength }}/500
              </span>
            </div>
          </div>
          <div class="flex items-center justify-between">
            <div class="flex-1">
              <div v-if="!canSubmit && remainingCriteriaMessage" class="text-sm text-gray-500">
                {{ remainingCriteriaMessage }}
              </div>
            </div>

            <Button
              :disabled="!canSubmit || isSubmitting || isCommentTooLong"
              @click="submitAllRatings"
            >
              <span v-if="isSubmitting">{{ $t('widget.rating.buttons.saving') }}</span>
              <span v-else-if="!canSubmit">{{ $t('widget.rating.buttons.rate_all') }}</span>
              <span v-else-if="submitError">{{ $t('widget.rating.buttons.try_again') }}</span>
              <span v-else>{{ $t('widget.rating.buttons.save') }}</span>
            </Button>
          </div>
        </template>
      </div>
    </ContentBlock>
  </section>
</template>
